import { Component, OnInit, inject } from "@angular/core";
import { Observable } from "rxjs";

import { delay, finalize, map, takeUntil, tap } from 'rxjs/operators';
import {
  ComparisonObjectsWithLogicalOperators,
  CustomResultOfPaginatedListOfGetPackageSalesForUserDto,
  GetEnumValuesDto,
  GetPackageSalesForUserDto,
  PaginatedListOfGetPackageSalesForUserDto,
} from 'src/app/core/api/models';
import {
  PackageOrganizationsService,
  PackageSalesService,
} from "src/app/core/api/services";
import { EventTypes } from "src/app/core/constants/event-types";
import { DialogStatus } from "src/app/core/services/dialog.service";
import { AppDomService } from "src/app/core/services/dom.service";
import { DynamicTabService } from "src/app/core/services/dynamic-tab.service";
import { RefreshMenuService } from "src/app/core/services/refresh-menu.service";
import { BaseIndexPageComponent } from "../../shared/base/components/base-index-page.component";
import { PackageOrganizationsFormComponent } from "./package-organizations-form/package-organizations-form.component";
import { PageNames } from "src/app/configs/page-names.enum";
import { BaseIndexModule } from "../../shared/base/modules/base-index.module";
import { PackageOrganizationsFilterFormComponent } from "./package-organizations-filter-form/package-organizations-filter-form.component";
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { PackageOrganizationCardComponent } from "./package-organizations-card/package-organizations-card.component";


@Component({
    selector: 'app-package-organizations',
    templateUrl: './package-organizations.component.html',
    styleUrls: [`./package-organizations.component.scss`],
    imports: [BaseIndexModule, PackageOrganizationsFilterFormComponent, InfiniteScrollModule, PackageOrganizationCardComponent]
})
export class PackageOrganizationsComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region properties

  pageName = PageNames.packageOrganizations;

  protected currentService = inject(PackageSalesService);
  paginationApiMethodName: string = this.currentService.packageSalesGetPackageSalesForUser.name;

  modelWithPagination$: Observable<PaginatedListOfGetPackageSalesForUserDto>;
  items$: Observable<Array<GetPackageSalesForUserDto>>;
  items: Array<GetPackageSalesForUserDto> = [];

  ExpirationType: GetEnumValuesDto[] = [];

  //milliseconds delay after the user stops scrolling;
  throttle = 300;
  // is event is triggered when 90% (100% - 10%) has been scrolled
  infiniteScrollDistance = 1; // 1 ==> 10%

  buyButtonLoading: boolean = false;

  //#endregion

  constructor(
    public organizationService: PackageOrganizationsService,
    public refreshMenuService: RefreshMenuService,
    private dynamicTabService: DynamicTabService
  ) {
    super();
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.pageConfig.showAddBtn = false;
      this.pageConfig.showAuditBtn = false;
      this.pageConfig.isShowTable = false;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }

  initTablePagination() {
    this.paginationModel.pageIndex = 1;
    this.paginationModel.firstIndex = 1;
    this.paginationModel.pageNumber = 1;
    this.paginationModel.pageSize = 12;
  }

  getItems() {
    try {
      this.modelWithPagination$
        .pipe(
          tap((item) => {
            this.getPaginationData(item);
          }),
          map((item) => item?.items),
          tap((items) => {
            items?.length > 0
              ? (this.pageConfig.isEmpty = false)
              : (this.pageConfig.isEmpty = true);
          }),
          takeUntil(this.onDestroy$),
          delay(300),
          finalize(() => {
            this.pageConfig.loading = false;
          })
        )
        .subscribe((items) => {
          this.items = this.items.concat(items);
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }

  configTableColumns(): void {}

  //#region api

  baseCreateGetApi(organizationPackagePrice: GetPackageSalesForUserDto) {
    this.unSubs();

    this.buyButtonLoading = true;

    this.organizationService
      .packageOrganizationsCreatePackageOrganizationGet()
      .pipe(
        finalize(() => {
          this.buyButtonLoading = false;
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe((res) => {
        if (res) this.onBuyPackage(organizationPackagePrice);
      });
  }

  //#endregion

  onBuyPackage(organizationPackagePrice: GetPackageSalesForUserDto): any {
    try {
      const modalResult = this.dialogService.openModal({
        component: PackageOrganizationsFormComponent,
        data: {
          organizationPackagePrice: organizationPackagePrice,
          vocabsPatch:
            (this.pageVocabs = `pages.${this.pageName}.packageOrganizationsCard.`),
        },
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.openPackageOrganizations();
          }
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }

  openPackageOrganizations() {
    try {
      this.dynamicTabService.setNewTab('');
      this.dynamicTabService.setNewTab('packageOrganizations');
      this.dynamicTabService.setActiveTab('packageOrganizations');
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }

  onShowDialog(result: any) {
    try {
      if (result.isSucceed) {
        this.dialogService.openDialog({
          title: result.type,
          message: result.title,
          status: DialogStatus.success,
        });
        this.openPackageOrganizations();
      } else {
        this.dialogService.openDialog({
          title: result.type,
          message: result.title,
          status: DialogStatus.error,
        });
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }

  onScrollDown() {
    try {
      const totalCount = this.paginationModel.totalCount;

      if (this.items.length == totalCount) return;

      const totalPages = this.paginationModel.totalPages;
      const pageNumber = this.paginationModel.pageNumber;

      if (this.pageConfig.loading) return;

      this.pageConfig.loading = true;

      if (pageNumber < totalPages) this.paginationModel.pageNumber++;

      this.getTableData();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }

  trackByFunc(index: number, item: GetPackageSalesForUserDto) {
    try {
      return item.id;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.packageOrganization
      );
    }
  }

  getComparisonObjects(model: ComparisonObjectsWithLogicalOperators[]) {
    this.items = [];
    this.paginationModel.pageNumber = 1;
    this.comparisonObjects = model;
    this.pageConfig.tableConfig.comparisonObjects = Object.assign(
      [],
      this.comparisonObjects
    );
    this.getTableData();
  }
}
